import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import CtaBanner from "@/components/CtaBanner";
import SEOHead from "@/components/SEOHead";

const STEPS = [
  {
    number: "01",
    title: "Apply Online in Under 3 Minutes",
    body: `You fill out one form at hylascompanion.com/apply. We ask for your name, contact info, CDL class, years of experience, and what type of position you're looking for. No lengthy questionnaires. No account creation. No application fee — ever. We also ask for basic employment history (your last three employers) and whether you have any violations we should know about upfront. Being honest at this stage saves everyone time. Most drivers complete the application in two to three minutes on their phone.`,
    aside: "No fee. No account. Just a form.",
  },
  {
    number: "02",
    title: "A Recruiter Calls You — Usually the Same Day",
    body: `After you submit, a real recruiter at Hylas Companion reviews your application and calls you — typically within one business day, often within hours during normal business hours. This is not a robocall and not a callback from a call center in another country. You'll talk to the same recruiter throughout the process. On the first call, your recruiter walks through your experience, asks about your preferred home time, your state of domicile, whether you have any program preferences (company driver, lease, regional, dedicated), and any restrictions or requirements you have (no cameras, specific equipment, home time minimums). This call typically runs 15–20 minutes.`,
    aside: "One recruiter. One contact. No call centers.",
  },
  {
    number: "03",
    title: "We Match You to Available Programs",
    body: `Based on your profile, your recruiter pulls available programs from across the Hylas Companion group that match your requirements. You'll be presented with 1–3 options, each with a plain-language breakdown: CPM or revenue share rate, approximate weekly mileage, home time cycle, trailer type, domicile city, and equipment model. If none of the options are right for you, your recruiter tells you honestly — including an estimated timeline for when a better match may become available. We don't pressure drivers to take programs that don't fit their life. A bad match leads to a driver leaving in 60 days, which helps no one.`,
    aside: "Honest options. No pressure.",
  },
  {
    number: "04",
    title: "Document Collection and Background Check",
    body: `Once you choose a program, your recruiter sends you a secure document checklist. Standard documents include your CDL (front and back), medical examiner's certificate, social security card or birth certificate, driving record (MVR), and PSP report if available. We run a standard background check and MVR pull — turnaround is usually 24–48 hours. For SAP drivers, your recruiter confirms which programs in the Hylas Companion group accept your status before this stage, not after. No surprises. Document review and background check is typically completed within 1–2 business days.`,
    aside: "1–2 business days to clear.",
  },
  {
    number: "05",
    title: "Orientation",
    body: `Orientation depends on your program type. For 1099 (company driver / lease / rent) programs, orientation is completed the same day — you arrive in the morning and are dispatched by afternoon, on the road making money the same day. A same-day cash advance is also available for 1099 drivers at orientation. For W2 positions (local, regional hourly), orientation runs 2–4 days and pays $300. For both program types, Hylas Companion covers transportation to orientation, accommodation, and meals — you arrive and leave with nothing out of pocket. Your truck is pre-assigned based on the program you chose; you'll know the year and model before you show up.`,
    aside: "1099: Same day, on the road by afternoon.\nW2: 2–4 days, $300 paid.\nTransportation, hotel & meals covered for both.",
  },
  {
    number: "06",
    title: "You're On the Road — With Support",
    body: `Once you're running, your Hylas Companion recruiter remains your direct contact for any program-related questions or concerns. Your day-to-day dispatch and load assignment is handled by our partner's operations team within the Hylas Companion group. If you have a problem with your program — pay discrepancy, equipment issue, home time not being honored — you contact your recruiter directly. We act as your advocate within the group. Most issues are resolved within one business day. We also check in at 30, 60, and 90 days to make sure the program is running as described.`,
    aside: "30/60/90-day check-ins. Direct recruiter contact forever.",
  },
];

const FAQS = [
  { q: "How long does the full process take?", a: "It depends on your program type. Document review and background check takes 1–2 business days. For 1099 programs (company driver, lease, rent) — orientation is done the same day, so most 1099 drivers are on the road and making money within 2–4 days of applying. For W2 positions, orientation runs 2–4 days, so most W2 drivers are running within 5–8 days total." },
  { q: "Do I need a referral or prior relationship with Hylas?", a: "No. Any driver — CDL or non-CDL — can apply directly at hylascompanion.com/apply. No referral required. No prior contact required." },
  { q: "What if I have violations on my record?", a: "Depends on the violation and when it occurred. Your recruiter reviews your record on the first call and tells you honestly which programs you qualify for. We work with SAP drivers on select Hylas Companion group programs. For DUI: we work with drivers who have a DUI, provided at least 1 year has passed since the conviction — this is the legal minimum under DOT regulations. Drivers within that 1-year window are not eligible. For other serious violations (at-fault fatality, etc.) — your recruiter will tell you plainly if your record is a barrier." },
  { q: "Can I bring a co-driver or team partner?", a: "Yes. If you have a co-driver, apply together and note the team preference in your application. We'll match you as a unit. Team seats run at $1 CPM per driver." },
  { q: "What if the program doesn't match what was described?", a: "Contact your Hylas recruiter directly — not just the operations team. This is what the recruiter relationship is for. We have direct access to the Hylas Companion group's operations teams and can escalate issues that wouldn't get traction if you contacted them alone." },
];

// ── GEO / Structured Data ─────────────────────────────────────────────────
const HOWITWORKS_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "HowTo",
      "@id": "https://hylascompanion.com/how-it-works#howto",
      "name": "How to Get Hired as a CDL-A Truck Driver at Hylas Companion",
      "description": "Complete step-by-step guide to Hylas Companion's CDL-A truck driver recruiting process. 1099 drivers (company/lease/rent) are typically on the road within 2–4 days of applying. No fees at any stage. Transportation, hotel, and meals covered for orientation.",
      "totalTime": "P4D",
      "estimatedCost": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": "0",
        "description": "No application fees, no placement fees, no startup costs — ever.",
      },
      "step": STEPS.map((s, i) => ({
        "@type": "HowToStep",
        "position": i + 1,
        "name": s.title,
        "text": s.body,
        "url": `https://hylascompanion.com/how-it-works#step-${s.number}`,
      })),
    },
    {
      "@type": "FAQPage",
      "@id": "https://hylascompanion.com/how-it-works#faq",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    },
    {
      "@type": "WebPage",
      "@id": "https://hylascompanion.com/how-it-works",
      "url": "https://hylascompanion.com/how-it-works",
      "name": "How Hylas Companion's Recruiting Process Works — Step by Step",
      "description": "From online application to first load: Hylas Companion places 1099 CDL-A drivers on the road in 2–4 days. One recruiter, no fees, orientation covered.",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", ".process-speakable"],
      },
      "inLanguage": "en-US",
    },
  ],
};

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="How Hylas Companion's Recruiting Process Works — Step by Step"
        description="Step-by-step guide to how Hylas Companion recruits CDL-A truck drivers. From online application to first load: what to expect, how long it takes, and how recruiter support works."
        path="/how-it-works"
        schema={HOWITWORKS_SCHEMA}
      />

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-40 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <span className="font-head text-[10px] uppercase tracking-[0.5em] text-primary">The Process</span>
          <h1 className="font-head text-5xl md:text-7xl font-bold uppercase tracking-[0.08em] text-white mt-4 leading-none max-w-3xl">
            How It<br />
            <span className="text-primary">Works</span>
          </h1>
          <p className="mt-6 text-white/55 text-base md:text-lg max-w-xl leading-relaxed">
            From your first application to your first load — exactly what happens, in what order, and how long each step takes. No vague promises.
          </p>
          <div className="flex flex-wrap gap-6 mt-10">
            {["1099: on the road within 2–4 days", "One recruiter throughout the process", "Transportation, hotel & meals covered for both 1099 and W2"].map((f) => (
              <div key={f} className="flex items-center gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                <span className="text-white/60 text-sm">{f}</span>
              </div>
            ))}
          </div>
          {/* GEO Citation block — AI-engine quotable summary */}
          <figure
            className="process-speakable mt-8 border border-white/10 p-5 max-w-lg"
            style={{ background: "rgba(245,200,66,0.04)" }}
            itemScope
            itemType="https://schema.org/Quotation"
            data-testid="geo-process-citation"
          >
            <p className="font-head text-[8px] uppercase tracking-[0.35em] text-primary/50 mb-2">Hylas Companion · Process Facts</p>
            <blockquote itemProp="text" className="text-sm text-white/55 leading-relaxed italic">
              &ldquo;Hylas Companion places most 1099 CDL-A drivers (company driver, lease, rent) on the road
              within 2–4 days of applying. Document review: 1–2 business days. Orientation: same day,
              dispatched by afternoon. Application fee: $0. Placement fee: $0. Orientation transport,
              hotel, and meals: covered.&rdquo;
            </blockquote>
            <figcaption className="mt-2 font-head text-[9px] uppercase tracking-[0.2em] text-white/20">
              — Hylas Companion · hylascompanion.com/how-it-works
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="how-it-works-steps">
        <div className="space-y-0 divide-y divide-border border border-border">
          {STEPS.map((s) => (
            <div key={s.number} className="grid md:grid-cols-[100px_1fr_220px] gap-0">
              {/* Step number */}
              <div className="flex items-start justify-center pt-10 pb-10 md:border-r border-border bg-[#0A0A0A]/[0.02]">
                <span className="font-head text-4xl font-bold text-primary/30">{s.number}</span>
              </div>
              {/* Body */}
              <div className="px-8 py-10 md:border-r border-border">
                <h2 className="font-head text-xl md:text-2xl font-bold uppercase tracking-[0.08em] text-[#0A0A0A] leading-tight mb-4">
                  {s.title}
                </h2>
                <p className="text-sm text-foreground/65 leading-[1.85]">{s.body}</p>
              </div>
              {/* Aside */}
              <div className="hidden md:flex items-center justify-center px-8 py-10 bg-[#0A0A0A]/[0.02]">
                <p className="font-head text-xs uppercase tracking-[0.2em] text-primary/70 text-center leading-relaxed">
                  {s.aside}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ strip */}
      <section className="bg-[#0A0A0A] py-20 md:py-28 px-6 md:px-12" data-testid="how-it-works-faq">
        <div className="max-w-7xl mx-auto">
          <span className="font-head text-[10px] uppercase tracking-[0.4em] text-primary">Quick Answers</span>
          <h2 className="font-head text-3xl md:text-4xl font-bold uppercase tracking-[0.1em] text-white mt-4 mb-12 leading-tight">
            Common Questions About The Process
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {FAQS.map((f) => (
              <div key={f.q} className="border border-white/10 p-7">
                <p className="font-head text-sm font-bold uppercase tracking-[0.1em] text-white mb-3">{f.q}</p>
                <p className="text-sm text-white/55 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 border border-border p-10 md:p-14">
          <div>
            <p className="font-head text-xs uppercase tracking-[0.4em] text-primary mb-3">Step 1 starts here</p>
            <h2 className="font-head text-3xl md:text-4xl font-bold uppercase tracking-[0.1em] text-[#0A0A0A] leading-tight">
              The application takes<br />under 3 minutes.
            </h2>
            <p className="mt-4 text-foreground/60 text-sm max-w-md leading-relaxed">
              No fee. No account. A recruiter calls you — usually the same day.
            </p>
          </div>
          <Link
            to="/apply"
            className="shrink-0 inline-flex items-center gap-3 font-head text-sm uppercase tracking-[0.25em] bg-[#0A0A0A] text-primary px-10 py-5 hover:bg-primary hover:text-black transition-colors duration-300"
          >
            Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
